import React from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

const MINUTES_IN_DAY = 1440;

function FormTimeSelector({ input, label, enableDays, inMinutes, meta: { touched, error } }) {
  const daysOptions = Array.from({ length: 31 }, (_, i) => i);
  const hoursOptions = Array.from({ length: 24 }, (_, i) => i);
  const minsOptions = Array.from({ length: 60 }, (_, i) => i);

  const [days, setDays] = React.useState(0);
  const [hours, setHours] = React.useState(0);
  const [minutes, setMinutes] = React.useState(0);

  React.useEffect(() => {
    const v = input.value;

    if (v === undefined || v === null || v === "") {
      setDays(0);
      setHours(0);
      setMinutes(0);
      return;
    }

    if (typeof v === "string" && v.includes(":")) {
      const [h, m] = v.split(":").map(Number);

      if (Number.isNaN(h) || Number.isNaN(m)) return;

      setDays(0);
      setHours(Math.min(Math.max(h, 0), 23));
      setMinutes(Math.min(Math.max(m, 0), 59));
      return;
    }

    if (typeof v === "number") {
      const safe = Math.max(0, v);

      const d = Math.floor(safe / MINUTES_IN_DAY);
      const h = Math.floor((safe % MINUTES_IN_DAY) / 60);
      const m = safe % 60;

      setDays(enableDays ? d : 0);
      setHours(h);
      setMinutes(m);
    }
  }, [input.value, enableDays]);

  const emitValue = (d, h, m) => {
    if (enableDays || inMinutes) {
      const total = d * MINUTES_IN_DAY + h * 60 + m;
      input.onChange(total);
      return;
    }

    const formatted = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

    input.onChange(formatted);
  };

  return (
    <MDBox>
      <MDTypography variant="caption" fontWeight="regular" color="text">
        {label}
      </MDTypography>

      <MDBox display="flex" gap={2} alignItems="center">
        {enableDays && (
          <>
            <Select
              variant="standard"
              value={days}
              onChange={(e) => {
                const v = Number(e.target.value);
                setDays(v);
                emitValue(v, hours, minutes);
              }}
              sx={{ width: "30px" }}
            >
              {daysOptions.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
            <MDTypography variant="caption">días</MDTypography>
          </>
        )}

        {/* HORAS */}
        <Select
          variant="standard"
          value={hours}
          onChange={(e) => {
            const v = Number(e.target.value);
            setHours(v);
            emitValue(days, v, minutes);
          }}
          sx={{ width: "30px" }}
        >
          {hoursOptions.map((h) => (
            <MenuItem key={h} value={h}>
              {String(h).padStart(2, "0")}
            </MenuItem>
          ))}
        </Select>
        <MDTypography variant="caption">h</MDTypography>

        {/* MINUTOS */}
        <Select
          variant="standard"
          value={minutes}
          onChange={(e) => {
            const v = Number(e.target.value);
            setMinutes(v);
            emitValue(days, hours, v);
          }}
          sx={{ width: "30px" }}
        >
          {minsOptions.map((m) => (
            <MenuItem key={m} value={m}>
              {String(m).padStart(2, "0")}
            </MenuItem>
          ))}
        </Select>
        <MDTypography variant="caption">min</MDTypography>
      </MDBox>

      {touched && error && (
        <MDTypography variant="caption" color="error">
          {error}
        </MDTypography>
      )}
    </MDBox>
  );
}

FormTimeSelector.propTypes = {
  input: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  meta: PropTypes.object.isRequired,
  enableDays: PropTypes.bool,
  inMinutes: PropTypes.bool,
};

FormTimeSelector.defaultProps = {
  enableDays: false,
  inMinutes: false,
};

export default FormTimeSelector;
